import { Suspense } from 'react';
import { ErrorComponent, RouterProvider, createRouter } from '@tanstack/react-router';
import { createRoot } from 'react-dom/client';
import { QueryCache, QueryClient, QueryClientProvider } from 'react-query';
import { authnClient } from '@frontend/auth';
import { getInitialParams } from '@frontend/env';
import { http } from '@frontend/fetch';
import { AlertsProvider, ThemeProvider } from '@frontend/design-system';
import { routeTree } from './routeTree.gen';
import { ErrorBoundary } from './shared/components/error-boundary/error-boundary';
import { Loader } from './shared/components/loader/loader';
import './index.css';

const sharedQueryCache = new QueryCache();

const queryClient = new QueryClient({
  queryCache: sharedQueryCache,
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60,
    },
  },
});

function initAuth() {
  // Get backend API from @frontend/env using getInitialParams (same pattern as weave)
  // This respects the environment switcher (localStorage override)
  const { backendApi } = getInitialParams();

  authnClient.setConfig({
    backendAPI: backendApi,
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // CURRENT: Legacy Auth
  // ═══════════════════════════════════════════════════════════════════════════
  // Air-Trainer uses legacy username/password auth via auth-api/v3/auth/verify
  authnClient.changeAuthMethod('legacy');

  // ═══════════════════════════════════════════════════════════════════════════
  // FUTURE: OKTA Auth Migration
  // ═══════════════════════════════════════════════════════════════════════════
  // TODO: When ready to migrate to OKTA:
  //
  // 1. Change auth method to 'okta':
  //    authnClient.changeAuthMethod('okta');
  //
  // 2. Configure OKTA settings from env vars:
  //    authnClient.setOktaConfig({
  //      clientId: appConfig.OKTA_CLIENT_ID,
  //      issuer: appConfig.OKTA_ISSUER,
  //    });
  //
  // 3. Update sign-in-view.tsx to redirect to OKTA login
  // 4. Handle OKTA callback in routing
  // 5. Remove legacy auth endpoint usage in src/api/auth-api.ts
  //
  // Note: OKTA env vars are already in publish.config.json:
  // - VITE_WEAVE_OKTA_CLIENT_ID
  // - VITE_WEAVE_OKTA_ISSUER (weaveworkforce.okta.com)
}

export const router = createRouter({
  routeTree,
  defaultPendingComponent: () => <Loader />,
  defaultErrorComponent: ({ error }: { error: unknown }) => <ErrorComponent error={error} />,
  context: {
    queryClient,
  },
});

// Router type registration disabled to avoid TS2717 conflict with shared route definitions.
// The router instance is correctly typed via createRouter.

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Root element with id 'root' not found.");
}

const root = createRoot(rootElement);

// Initialize auth
initAuth();

// Set base URL for all HTTP calls - same pattern as weave
// This propagates to the entire app
if (!http.baseUrl) {
  const { backendApi } = getInitialParams();
  http.setBaseUrl(backendApi);
}

root.render(
  <Suspense fallback={<Loader />}>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider includeEmotionTheme>
          <AlertsProvider>
            <RouterProvider router={router} />
          </AlertsProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </Suspense>
);
